import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useEffect, useMemo } from 'react';
import { useIsClient } from 'usehooks-ts';

import { useAppSelector } from '@store/hooks';
import { darkReaderIgnoreInlineStyleClass } from '@components/dark-mode/dark-mode-provider';
import TruncateText from '@components/truncate-text';
import { CUSTOM_BANNER_TEXT_LIMIT } from '@features/config/configs.const';

import { useAppContext } from '../app-context';

import s from './custom-banner.scss';

const BANNER_HEIGHT = '3rem';
const cssVariables = {
  top: '--anchore-custom-banner-top-height',
  bottom: '--anchore-custom-banner-bottom-height',
};
interface CustomBannerConfig {
  display: 'always' | 'login-only';
  text: string;
  textColor?: string;
  backgroundColor?: string;
}

type CustomBannerType = 'top' | 'bottom';

export type CustomBannersConfig = Partial<
  Record<CustomBannerType, CustomBannerConfig | null>
>;

interface CustomBannerProps {
  type: CustomBannerType;
  config: CustomBannerConfig | null;
}

const useCustomBanners = () => {
  const { customBanners } = useAppContext();

  const topBannerConfig = customBanners?.top || null;
  const bottomBannerConfig = customBanners?.bottom || null;

  return { topBannerConfig, bottomBannerConfig };
};

const CustomBanner = ({ type, config }: CustomBannerProps) => {
  useStyles(s);

  const isClient = useIsClient();

  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);

  const showBanner = useMemo(() => {
    if (!config || !config.text) return false;
    if (config.display === 'always' || !config.display) return true;
    if (config.display === 'login-only' && !isAuthenticated) return true;
    return false;
  }, [config, isAuthenticated]);

  useEffect(() => {
    if (!isClient) return;

    const root = document.querySelector<HTMLElement>(':root');

    const currentValue =
      root && getComputedStyle(root).getPropertyValue(cssVariables[type]);
    const newValue = showBanner ? BANNER_HEIGHT : '0px';

    if (root && currentValue !== newValue) {
      root.style.setProperty(cssVariables[type], newValue);
    }
  }, [isClient, showBanner]);

  return config && showBanner ? (
    <div
      className={classNames({
        [s.customBanner]: true,
        [s.top]: type === 'top',
        [s.bottom]: type === 'bottom',
        [darkReaderIgnoreInlineStyleClass]: true,
      })}
      style={{
        color: config.textColor,
        backgroundColor: config.backgroundColor,
      }}
    >
      <TruncateText
        position={type === 'top' ? 'bottom center' : 'top center'}
        flowing
        className={s.textPopup}
      >
        {config.text.substring(0, CUSTOM_BANNER_TEXT_LIMIT)}
      </TruncateText>
    </div>
  ) : null;
};

export const CustomBanners = () => {
  const { topBannerConfig, bottomBannerConfig } = useCustomBanners();

  return (
    <>
      {/* Always render the banners to ensure the CSS variables are set */}
      <CustomBanner type="top" config={topBannerConfig} />
      <CustomBanner type="bottom" config={bottomBannerConfig} />
    </>
  );
};
